/**
 * The route registry — one eager table the whole app is derived from:
 * both route trees (`/` live, `/test` mock), the sidebar, breadcrumbs, the
 * top bar's controls and whether a page draws a filters bar.
 *
 * Replaces the prototype's hash router, which derived routes from the
 * sidebar's *story fixtures*.
 *
 * `live: false` keeps a page out of the `/` tree (and its sidebar row hidden)
 * until its widgets have real API loaders. Flip it only when
 * `pnpm test` (live-route gate) passes — see docs/taking-a-page-live.md.
 */
import type { IconName } from '@deepdive-ui';
import type { ComponentType } from 'react';

import type { FiltersType, WidgetId } from '@/data-sources/contract';

/** The top bar's shape per page (the prototype's `HeaderView.barFor`). */
export type TopBarKind =
  | 'module' // date range + comparison (Export hidden until an export exists)
  | 'reports-list' // Create a report
  | 'report-detail' // date range + comparison, no CTA
  | 'deepdive-home' // Chat history
  | 'deepdive-history' // nothing
  | 'deepdive-chat' // Chat history + New chat
  | 'settings' // Cancel + Save changes
  | 'quick-search' // breadcrumb only
  | 'influencer-detail'; // date range only

export type NavSectionId = 'top' | 'monitor' | 'intelligence' | 'footer' | 'executive';

/**
 * Which product a page belongs to. The executive dashboard is a separate,
 * smaller product for a client's top management (GP's, 2026-10-27): its own
 * pages under `/executive`, its own sidebar, the same widgets underneath.
 */
export type AppView = 'analyst' | 'executive';

export type PageModule = { default: ComponentType };

export type AppRoute = {
  /** Stable id; also the sidebar item that lights up. */
  id: string;
  /** Path relative to the tree base, e.g. '' (index), 'mentions', 'custom-reports/:reportId'. */
  path: string;
  page: () => Promise<PageModule>;
  /** The sidebar row this route belongs under (defaults to the module id). */
  navItem: string;
  filters: FiltersType | null;
  topBar: TopBarKind;
  /**
   * Trailing breadcrumbs beyond "Section > Module" — a dynamic crumb (report
   * name, chat title…) is resolved by `useCrumbLabel` in the top bar.
   */
  crumbs?: readonly CrumbSpec[];
  /**
   * The analytics widgets this page renders. Required before `live: true`:
   * the live-route test fails if any of them has no live loader yet.
   */
  widgets?: readonly WidgetId[];
  /** A floating actions row pinned to the window (Shell `dock`). */
  dock?: 'report-actions';
  /** Mounted under `/`? */
  live: boolean;
  /** Set on executive dashboard pages; every other page is the analyst product. */
  view?: 'executive';
};

export type CrumbSpec =
  | { kind: 'static'; label: string }
  | {
      kind: 'param';
      param: string;
      resolve: 'report' | 'chat' | 'influencer' | 'industry' | 'quick-search';
    };

/** A sidebar row. Order here is sidebar order (Reef `sidebar.fixtures.ts`). */
export type NavItem = {
  id: string;
  label: string;
  icon: IconName;
  section: NavSectionId;
  /** Canonical path (no base), e.g. '/mentions'. */
  href: string;
  badge?: { type: 'info' | 'positive'; label: string };
};

export type NavSection = { id: Exclude<NavSectionId, 'footer'>; label?: string };

export const NAV_SECTIONS: readonly NavSection[] = [
  { id: 'top' },
  { id: 'monitor', label: 'Monitor' },
  { id: 'intelligence', label: 'Intelligence' },
];

/** The executive dashboard's sidebar: one unlabelled list, in report order. */
export const EXECUTIVE_NAV_SECTIONS: readonly NavSection[] = [{ id: 'executive' }];

export const viewOf = (route: AppRoute | undefined): AppView => route?.view ?? 'analyst';

export const navSectionsFor = (view: AppView): readonly NavSection[] =>
  view === 'executive' ? EXECUTIVE_NAV_SECTIONS : NAV_SECTIONS;

export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'overview', label: 'Overview', icon: 'overview', section: 'top', href: '/' },
  {
    id: 'custom-reports',
    label: 'Custom reports',
    icon: 'customReport',
    section: 'top',
    href: '/custom-reports',
  },
  {
    id: 'deepdive-ai',
    label: 'DeepDive AI',
    icon: 'deepdiveAi',
    section: 'top',
    href: '/deepdive-ai',
    badge: { type: 'info', label: 'Beta' },
  },
  {
    id: 'quick-search',
    label: 'Quick search',
    icon: 'quickSearch',
    section: 'top',
    href: '/quick-search',
  },
  { id: 'mentions', label: 'Mentions', icon: 'socialFeed', section: 'monitor', href: '/mentions' },
  {
    id: 'influencers',
    label: 'Influencers',
    icon: 'influencers',
    section: 'monitor',
    href: '/influencers',
  },
  { id: 'media', label: 'Media', icon: 'media', section: 'monitor', href: '/media' },
  {
    id: 'brand-health',
    label: 'Brand health',
    icon: 'brandHealth',
    section: 'intelligence',
    href: '/brand-health',
  },
  {
    id: 'community',
    label: 'Community',
    icon: 'audience',
    section: 'intelligence',
    href: '/community',
  },
  {
    id: 'competitors',
    label: 'Competitors',
    icon: 'competitors',
    section: 'intelligence',
    href: '/competitors',
  },
  { id: 'trends', label: 'Trends', icon: 'trends', section: 'intelligence', href: '/trends' },
  { id: 'settings', label: 'Settings', icon: 'settings', section: 'footer', href: '/settings' },
  /* The executive dashboard, in the order of the monthly report's sections. */
  {
    id: 'exec-dashboard',
    label: 'Executive dashboard',
    icon: 'overview',
    section: 'executive',
    href: '/executive',
  },
  {
    id: 'exec-conversation',
    label: 'Conversation landscape',
    icon: 'conversations',
    section: 'executive',
    href: '/executive/conversation-landscape',
  },
  {
    id: 'exec-voice',
    label: 'Customer voice',
    icon: 'replies',
    section: 'executive',
    href: '/executive/customer-voice',
  },
  {
    id: 'exec-sentiment',
    label: 'Sentiment overview',
    icon: 'sentiment',
    section: 'executive',
    href: '/executive/sentiment',
  },
  {
    id: 'exec-competitive',
    label: 'Competitive intelligence',
    icon: 'competitors',
    section: 'executive',
    href: '/executive/competitive-intelligence',
  },
  {
    id: 'exec-official-pages',
    label: 'Official pages',
    icon: 'brand',
    section: 'executive',
    href: '/executive/official-pages',
  },
  {
    id: 'exec-campaigns',
    label: 'Campaigns and content',
    icon: 'campaign',
    section: 'executive',
    href: '/executive/campaigns',
  },
  /* The one analyst module executives get, after the report's sections (GP, 2026-10-28). */
  {
    id: 'exec-ai',
    label: 'DeepDive AI',
    icon: 'deepdiveAi',
    section: 'executive',
    href: '/executive/deepdive-ai',
    badge: { type: 'info', label: 'Beta' },
  },
];

const page = (load: () => Promise<PageModule>) => load;

export const APP_ROUTES: readonly AppRoute[] = [
  {
    id: 'overview',
    path: '',
    navItem: 'overview',
    page: page(() => import('@/pages/Overview')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'total-mentions',
      'share-of-voice-figure',
      'total-unique-audience',
      'negative-conversation',
      'nps',
      'activity-by-day-and-hour',
      'share-of-voice',
      'complaints-by-division',
      'community-topics',
      'mention-volume-over-time',
      'competitor-sentiment-comparison',
      'trending-keywords',
      'topic-sentiment-split',
    ],
    live: true,
  },
  {
    id: 'custom-reports',
    path: 'custom-reports',
    navItem: 'custom-reports',
    page: page(() => import('@/pages/CustomReports')),
    filters: null,
    topBar: 'reports-list',
    widgets: [],
    live: true,
  },
  {
    id: 'custom-report',
    path: 'custom-reports/:reportId',
    navItem: 'custom-reports',
    page: page(() => import('@/pages/ReportDetail')),
    filters: 'competitors',
    topBar: 'report-detail',
    crumbs: [{ kind: 'param', param: 'reportId', resolve: 'report' }],
    dock: 'report-actions',
    widgets: [],
    live: true,
  },
  {
    id: 'deepdive-ai',
    path: 'deepdive-ai',
    navItem: 'deepdive-ai',
    page: page(() => import('@/pages/DeepDive')),
    filters: null,
    topBar: 'deepdive-home',
    widgets: [],
    live: true,
  },
  {
    id: 'deepdive-history',
    path: 'deepdive-ai/history',
    navItem: 'deepdive-ai',
    page: page(() => import('@/pages/ChatHistory')),
    filters: null,
    topBar: 'deepdive-history',
    crumbs: [{ kind: 'static', label: 'Chat history' }],
    widgets: [],
    live: true,
  },
  {
    id: 'deepdive-chat',
    path: 'deepdive-ai/:chatId',
    navItem: 'deepdive-ai',
    page: page(() => import('@/pages/DeepDiveChat')),
    filters: null,
    topBar: 'deepdive-chat',
    crumbs: [{ kind: 'param', param: 'chatId', resolve: 'chat' }],
    widgets: [],
    live: true,
  },
  {
    id: 'quick-search',
    path: 'quick-search',
    navItem: 'quick-search',
    page: page(() => import('@/pages/QuickSearch')),
    filters: null,
    topBar: 'quick-search',
    widgets: [],
    live: true,
  },
  {
    id: 'quick-search-entry',
    path: 'quick-search/:historyEntryId',
    navItem: 'quick-search',
    page: page(() => import('@/pages/QuickSearchEntry')),
    filters: null,
    topBar: 'quick-search',
    crumbs: [{ kind: 'param', param: 'historyEntryId', resolve: 'quick-search' }],
    widgets: [],
    live: true,
  },
  {
    id: 'mentions',
    path: 'mentions',
    navItem: 'mentions',
    page: page(() => import('@/pages/Mentions')),
    filters: 'default',
    topBar: 'module',
    widgets: ['mention-volume-over-time', 'mentions-by-platform', 'intent-based-mentions'],
    live: true,
  },
  {
    id: 'influencers',
    path: 'influencers',
    navItem: 'influencers',
    page: page(() => import('@/pages/Influencers')),
    filters: null,
    topBar: 'module',
    widgets: ['influencer-analytics'],
    live: true,
  },
  {
    id: 'influencer',
    path: 'influencers/:influencerId',
    navItem: 'influencers',
    page: page(() => import('@/pages/InfluencerDetail')),
    filters: null,
    topBar: 'influencer-detail',
    crumbs: [{ kind: 'param', param: 'influencerId', resolve: 'influencer' }],
    widgets: ['influencer-profile', 'top-performing-content'],
    live: true,
  },
  {
    id: 'influencer-industry',
    path: 'influencers/:influencerId/:industry',
    navItem: 'influencers',
    page: page(() => import('@/pages/IndustryDetail')),
    filters: null,
    topBar: 'influencer-detail',
    crumbs: [
      { kind: 'param', param: 'influencerId', resolve: 'influencer' },
      { kind: 'param', param: 'industry', resolve: 'industry' },
    ],
    widgets: ['influencer-industry', 'top-performing-content'],
    live: true,
  },
  {
    id: 'media',
    path: 'media',
    navItem: 'media',
    page: page(() => import('@/pages/Media')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'media-total-mentions',
      'media-positive-mentions',
      'media-negative-mentions',
      'top-media-outlets',
      'media-sentiment-over-time',
    ],
    live: true,
  },
  {
    id: 'brand-health',
    path: 'brand-health',
    navItem: 'brand-health',
    page: page(() => import('@/pages/BrandHealth')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'nps',
      'seeded-vs-organic',
      'sentiment-over-time',
      'engagement-volume',
      'engagement-by-type',
      'topic-sentiment-split',
      'engagement-growth',
    ],
    live: true,
  },
  {
    id: 'community',
    path: 'community',
    navItem: 'community',
    page: page(() => import('@/pages/Community')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'top-communities-by-mention',
      'activity-by-day-and-hour',
      'top-voices-table',
      'community-activity-timeline',
      'audience-age-and-gender',
      'complaints-by-division',
      'top-individual-voices',
      'top-communities-by-sentiment',
      'top-communities-table',
      'community-topics',
    ],
    live: true,
  },
  {
    id: 'competitors',
    path: 'competitors',
    navItem: 'competitors',
    page: page(() => import('@/pages/Competitors')),
    filters: 'competitors',
    topBar: 'module',
    widgets: [
      'share-of-voice',
      'share-of-positive',
      'share-of-voice-over-time',
      'competitive-page-performance',
      'mention-volume-vs-competitors',
      'competitor-sentiment-comparison',
      'top-performing-content',
    ],
    live: true,
  },
  {
    id: 'trends',
    path: 'trends',
    navItem: 'trends',
    page: page(() => import('@/pages/Trends')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'top-hashtags',
      'keyword-sentiment',
      'engagement-spikes',
      'trending-topics',
      'trending-keywords',
      'keyword-mentions-frequency',
    ],
    live: true,
  },
  /*
   * The executive dashboard (prototype). Brands is on screen and starts at the
   * own brand alone; the pages read every operator and draw the brands picked
   * (src/features/executive). Not live until executives can be told apart.
   */
  {
    id: 'exec-dashboard',
    path: 'executive',
    navItem: 'exec-dashboard',
    page: page(() => import('@/pages/ExecutiveDashboard')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'total-mentions',
      'total-unique-audience',
      'share-of-voice-figure',
      'share-of-positive',
      'negative-conversation',
      'competitive-page-performance',
      'share-of-voice',
      'mention-volume-over-time',
      'trending-topics',
      'topic-sentiment-split',
      'keyword-mentions-frequency',
      'sentiment-over-time',
      'competitor-sentiment-comparison',
      'top-performing-content',
    ],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-conversation',
    path: 'executive/conversation-landscape',
    navItem: 'exec-conversation',
    page: page(() => import('@/pages/ExecutiveConversation')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'total-mentions',
      'total-unique-audience',
      'engagement-volume',
      'mention-volume-over-time',
      'mentions-by-platform',
      'share-of-voice',
      'share-of-voice-over-time',
      'unique-authors-by-brand',
      'engagement-by-brand',
    ],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-voice',
    path: 'executive/customer-voice',
    navItem: 'exec-voice',
    page: page(() => import('@/pages/ExecutiveVoice')),
    filters: 'default',
    topBar: 'module',
    widgets: [
      'topic-sentiment-split',
      'intent-based-mentions',
      'top-performing-content',
      'keyword-sentiment',
    ],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-sentiment',
    path: 'executive/sentiment',
    navItem: 'exec-sentiment',
    page: page(() => import('@/pages/ExecutiveSentiment')),
    filters: 'competitors',
    topBar: 'module',
    widgets: [
      'share-of-positive',
      'sentiment-over-time',
      'mentions-by-platform',
      'topic-sentiment-split',
      'competitor-sentiment-comparison',
    ],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-competitive',
    path: 'executive/competitive-intelligence',
    navItem: 'exec-competitive',
    page: page(() => import('@/pages/ExecutiveCompetitive')),
    filters: 'competitors',
    topBar: 'module',
    widgets: [
      'share-of-voice',
      'share-of-positive',
      'share-of-voice-over-time',
      'competitor-sentiment-comparison',
      'competitive-page-performance',
      'top-performing-content',
      'topic-ownership',
    ],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-official-pages',
    path: 'executive/official-pages',
    navItem: 'exec-official-pages',
    page: page(() => import('@/pages/ExecutiveOfficialPages')),
    filters: 'default',
    topBar: 'module',
    widgets: ['competitive-page-performance'],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-campaigns',
    path: 'executive/campaigns',
    navItem: 'exec-campaigns',
    page: page(() => import('@/pages/ExecutiveCampaigns')),
    filters: 'competitors',
    topBar: 'module',
    widgets: [
      'total-mentions',
      'engagement-volume',
      'share-of-positive',
      'mention-volume-over-time',
      'engagement-by-type',
      'top-performing-content',
      'top-hashtags',
      'mentions-by-platform',
    ],
    view: 'executive',
    live: true,
  },
  /*
   * DeepDive AI inside the executive view: the analyst module's own pages under
   * `/executive`, so the executive sidebar stays. Their links are written to the
   * analyst paths and land here (`paths.ts`, `EXECUTIVE_HOSTED`).
   */
  {
    id: 'exec-ai',
    path: 'executive/deepdive-ai',
    navItem: 'exec-ai',
    page: page(() => import('@/pages/DeepDive')),
    filters: null,
    topBar: 'deepdive-home',
    widgets: [],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-ai-history',
    path: 'executive/deepdive-ai/history',
    navItem: 'exec-ai',
    page: page(() => import('@/pages/ChatHistory')),
    filters: null,
    topBar: 'deepdive-history',
    crumbs: [{ kind: 'static', label: 'Chat history' }],
    widgets: [],
    view: 'executive',
    live: true,
  },
  {
    id: 'exec-ai-chat',
    path: 'executive/deepdive-ai/:chatId',
    navItem: 'exec-ai',
    page: page(() => import('@/pages/DeepDiveChat')),
    filters: null,
    topBar: 'deepdive-chat',
    crumbs: [{ kind: 'param', param: 'chatId', resolve: 'chat' }],
    widgets: [],
    view: 'executive',
    live: true,
  },
  {
    id: 'settings',
    path: 'settings/:tab',
    navItem: 'settings',
    page: page(() => import('@/pages/Settings')),
    filters: null,
    topBar: 'settings',
    widgets: [],
    live: true,
  },
];

export const routeById = new Map(APP_ROUTES.map((route) => [route.id, route]));
export const navItemById = new Map(NAV_ITEMS.map((item) => [item.id, item]));

/** Section label for breadcrumbs ("Monitor", "Intelligence"), if the nav item has one. */
export function sectionLabelOf(navItemId: string): string | undefined {
  const item = navItemById.get(navItemId);
  return NAV_SECTIONS.find((section) => section.id === item?.section)?.label;
}
